import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TEBEX_URL = 'https://devcore.tebex.io/';
const DISCORD_URL = 'https://discord.gg/zcG9KQj3sa';

function Home() {
  const location = useLocation();
  const [banMessage, setBanMessage] = useState('');
  const [partners, setPartners] = useState([]);
  const [releases, setReleases] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    
    if (error === 'ip_banned') {
      setBanMessage('Your IP address has been banned from accessing this website. Contact support if you believe this is a mistake.');
    } else if (error === 'account_banned') {
      setBanMessage('Your account has been banned. Contact support for more information.');
    }

    fetchPartners();
    fetchReleases();
  }, [location]);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    }
  };

  const fetchReleases = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/releases`);
      const releasesData = response.data.map(r => ({
        ...r,
        tags: r.tags ? JSON.parse(r.tags) : []
      }));
      setReleases(releasesData.slice(0, 6)); // Get latest 6
    } catch (error) {
      console.error('Error fetching releases:', error);
      setReleases([]); // Set empty array on error
    }
  };

  return (
    <div className="home">
      {banMessage && (
        <div className="ban-alert">
          <div className="ban-alert-content">
            <span className="ban-icon">🚫</span>
            <p>{banMessage}</p>
            <button onClick={() => setBanMessage('')} className="close-alert">✕</button>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content fade-in">
            <h1 className="hero-title">
              High Quality Scripts
            </h1>
            <div className="hero-subtitle">
              <p>We create original scripts directly for you and your server</p>
              <p>Our scripts focus mostly on Roleplay and try to make the game more realistic</p>
              <p>All our products are thoroughly tested before release</p>
              <p>We correct every error found and gradually improve each script</p>
            </div>
            <div className="hero-buttons">
              <a href={TEBEX_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">Browse Scripts</a>
              <a href="/documentation" className="btn btn-secondary">Documentation</a>
            </div>
          </div>
        </div>
      </section>

      {/* Releases Section */}
      <section className="releases-section">
        <div className="container">
          <div className="home-stats-grid">
            <div className="home-stat-card glass">
              <h3>3000+</h3>
              <p>Total Sales</p>
            </div>
            <div className="home-stat-card glass">
              <h3>500+</h3>
              <p>Servers Using Our Scripts</p>
            </div>
            <div className="home-stat-card glass">
              <h3>3700+</h3>
              <p>Discord Members</p>
            </div>
          </div>

          <h2 className="section-title">Latest Releases</h2>
          <div className="releases-grid">
            {releases.length > 0 ? (
              releases.map((item) => (
                <div key={item.id} className="release-card glass">
                  {item.image_url && (
                    <div className="release-image" style={{ backgroundImage: `url(${item.image_url})` }}></div>
                  )}
                  <div className="release-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    {item.tags && item.tags.length > 0 && (
                      <div className="release-tags">
                        {item.tags.map((tag, idx) => (
                          <span key={idx} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    {item.tebex_url && (
                      <a href={item.tebex_url} className="release-link" target="_blank" rel="noopener noreferrer">
                        View on Tebex →
                      </a>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-content">No releases available yet</p>
            )}
          </div>
        </div>
      </section>

      {/* Partners Carousel */}
      {partners.length > 0 && (
        <section className="partners-carousel-section">
          <div className="container">
            <h2 className="section-title">Partners</h2>
            {partners.length < 3 ? (
              <div className="partners-static">
                {partners.map((partner) => (
                  <div key={partner.id} className="partner-carousel-card">
                    {partner.logo ? (
                      <img src={partner.logo} alt={partner.name} className="partner-carousel-logo" />
                    ) : (
                      <div className="partner-carousel-placeholder">{partner.name.charAt(0)}</div>
                    )}
                    <h4 className="partner-carousel-name">{partner.name}</h4>
                    {partner.discord_url && (
                      <a href={partner.discord_url} target="_blank" rel="noopener noreferrer" className="partner-carousel-discord">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/></svg>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
            <div className="partners-carousel">
              <div className="partners-track">
                {[...partners, ...partners, ...partners].map((partner, idx) => (
                  <div key={`${partner.id}-${idx}`} className="partner-carousel-card">
                      {partner.logo ? (
                        <img src={partner.logo} alt={partner.name} className="partner-carousel-logo" />
                      ) : (
                        <div className="partner-carousel-placeholder">{partner.name.charAt(0)}</div>
                      )}
                      <h4 className="partner-carousel-name">{partner.name}</h4>
                      {partner.discord_url && (
                        <a href={partner.discord_url} target="_blank" rel="noopener noreferrer" className="partner-carousel-discord">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content glass">
            <h2>Ready to upgrade your server?</h2>
            <p>Browse our collection of premium scripts and take your server to the next level</p>
            <div className="cta-buttons">
              <a href={TEBEX_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">View All Scripts</a>
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                Join Discord
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
